import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
} from 'react-query';
import {
  Approval,
  CreateApprovalPayload,
  UpdateApprovalPayload,
} from 'api/approval';
import { TaskId } from 'api/models';
import { TaskApprovalAPI } from 'api/tasks';
import { tenMinutes, HOME_QUERY_KEY } from './constants';

export const getTaskApprovalsQueryKey = (taskId: TaskId) => [
  'approvals',
  ...taskId,
];
export const getProjectApprovalsQueryKey = (projectId: number) => [
  'approvals',
  projectId,
];
export const useTaskApprovalsQuery = (taskId: TaskId) => {
  return useQuery(
    getTaskApprovalsQueryKey(taskId),
    () => TaskApprovalAPI.getApprovalList(taskId[1], taskId[2]),
    {
      staleTime: tenMinutes,
    },
  );
};
export const useProjectApprovalQuery = (projectId: number) => {
  return useQuery(
    getProjectApprovalsQueryKey(projectId),
    () => TaskApprovalAPI.getApproval(projectId),
    {
      staleTime: 0,
      cacheTime: 0,
    },
  );
};

export const useCreateTaskApprovalMutation = (
  id: TaskId,
): UseMutationResult<Approval, unknown, CreateApprovalPayload> => {
  const [, projectId, taskId] = id;
  const queryClient = useQueryClient();

  return useMutation<Approval, unknown, CreateApprovalPayload>(
    (payload) => TaskApprovalAPI.createApproval(projectId, taskId, payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getTaskApprovalsQueryKey(id));
      },
    },
  );
};

export const useUpdateTaskApprovalMutation = ([
  ,
  projectId,
  taskId,
]: TaskId): UseMutationResult<Approval, unknown, UpdateApprovalPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Approval, unknown, UpdateApprovalPayload>(
    (payload) =>
      TaskApprovalAPI.updateApproval(
        projectId,
        taskId,
        payload.approvalId,
        payload,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getTaskApprovalsQueryKey(TaskId(projectId, taskId)),
        );
        await queryClient.invalidateQueries(HOME_QUERY_KEY);
      },
    },
  );
};

export const useDeleteTaskApprovalMutation = ([
  ,
  projectId,
  taskId,
]: TaskId): UseMutationResult<void, unknown, number> => {
  const queryClient = useQueryClient();

  return useMutation(
    (approvalId) =>
      TaskApprovalAPI.deleteApproval(projectId, taskId, approvalId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getTaskApprovalsQueryKey(TaskId(projectId, taskId)),
        );
      },
    },
  );
};
